import type { SagaIterator } from 'redux-saga';
import { put, select, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { datasetsActions } from '../datasets';
import { featureFlagsSelectors } from '../feature-flags';

import type { CoreActionCreator } from '../core/resourceStoreTypes';
import * as actions from './tagsActions';
import { getLastSyncDate } from './tagsSelectors';
import { transformResponseToTags, transformTagToRequestData, transformTagsToRequestData } from './tagsTransformers';
import * as tagsTypes from './tagsTypes';

export function* tagsDumpUpdater(resources: Array<tagsTypes.TagProps>): SagaIterator {
  const bumpTags = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpTags');
  if (bumpTags) {
    const tags = resources.map((resource) => tagsTypes.mkNewTag(resource));
    yield put(actions.getTagsSuccess({ resources: tags }, { context: 'BUMP_CHANGE_DUMP' }));
  }
}

export function* tagsNudgeUpdater(): SagaIterator {
  const bumpAccounts = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpTags');
  if (bumpAccounts) {
    yield put(actions.getTags(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

const mkQcsSyncTagsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<tagsTypes.TagProps>();

const mkResourceConfig = (successAction: CoreActionCreator<any, any>, failureAction: CoreActionCreator<any, any>) =>
  mkQcsSyncTagsConfig({
    resourceName: 'tag',
    resourceBaseUrl: '/tags',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformResponseToResources: transformResponseToTags,
    transformResourceToRequestData: transformTagToRequestData,
    transformResourcesToRequestData: transformTagsToRequestData,

    successAction,
    failureAction,
  });

export function* getTagsActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.getTagsSuccess, actions.getTagsFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getTags,
    resourceSagaUtils.qcsSyncGetResources<tagsTypes.TagProps>,
    resourceConfig,
  );
}

export function* createTagActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.createTagSuccess, actions.createTagFailure);
  yield takeEvery(actions.createTag.type, resourceSagaUtils.qcsSyncCreateResource<tagsTypes.TagProps>, resourceConfig);
}

export function* batchTagsActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.batchTagsSuccess, actions.batchTagsFailure);
  yield takeEvery(actions.batchTags.type, resourceSagaUtils.qcsSyncUpdateResources<tagsTypes.TagProps>, resourceConfig);
}

export function* updateTagActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.updateTagSuccess, actions.updateTagFailure);
  yield takeEvery(actions.updateTag.type, resourceSagaUtils.qcsSyncUpdateResource<tagsTypes.TagProps>, resourceConfig);
}

export function* deleteTagActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.deleteTagSuccess, actions.deleteTagFailure);
  yield takeEvery(actions.deleteTag.type, resourceSagaUtils.qcsSyncDeleteResource<tagsTypes.TagProps>, resourceConfig);
}

export function* tagsFailureActionWatcher() {
  yield takeEvery(
    [
      actions.createTagFailure.type,
      actions.updateTagFailure.type,
      actions.deleteTagFailure.type,
      actions.batchTagsFailure.type,
    ],
    actions.getTags,
  );
}

function* datasetPollSuccessActionWatcher() {
  yield takeEvery(datasetsActions.datasetPollSuccess, function* getTags() {
    yield put(actions.getTags());
  });
}

export default [
  getTagsActionWatcher,
  createTagActionWatcher,
  batchTagsActionWatcher,
  updateTagActionWatcher,
  deleteTagActionWatcher,
  datasetPollSuccessActionWatcher,
  tagsFailureActionWatcher,
];
